import React from 'react';
import { CreditCard, Truck, RotateCcw, ShieldCheck } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <section className="bg-slate-50 min-h-screen border-t border-slate-200">
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-16">
        {/* Page header */}
        <div className="text-center mb-10">
          <p className="text-xs tracking-[0.3em] uppercase text-slate-500 mb-8">
        
          </p>
          <h1 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-3">
            Payment, Shipping, Returns &amp; Privacy
          </h1>
       
        </div>

        {/* Payment & Shipping cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Payment */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-slate-900 flex items-center justify-center">
                <CreditCard className="h-5 w-5 text-white" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900">
                Payment Accepted &amp; Terms
              </h2>
            </div>
            <ul className="space-y-2 text-sm text-slate-600">
              <li>
                <span className="font-semibold">Payment Accepted:</span>{' '}
                We accept payments via cheque and mobile / net banking,
                including mobile banking options.
              </li>
              <li>
                <span className="font-semibold">Payment Terms for Orders:</span>{' '}
                Orders require{' '}
                <span className="font-semibold">Partial Payment Advance (PPA)</span>{' '}
                or <span className="font-semibold">Cash on Delivery (COD)</span> upon
                purchase order (PO) confirmation, as agreed at the time of order.
              </li>
            </ul>
          </div>

          {/* Shipping & Packaging */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-slate-900 flex items-center justify-center">
                <Truck className="h-5 w-5 text-white" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900">
                Shipping, Shipment &amp; Packaging
              </h2>
            </div>
            <ul className="space-y-2 text-sm text-slate-600">
              <li>
                <span className="font-semibold">Packaging:</span>{' '}
                Products are packed using customised packaging solutions such as
                carry bags and chipboard packaging to help ensure they arrive
                safely and presentably.
              </li>
              <li>
                <span className="font-semibold">Shipping / Shipment Services:</span>{' '}
                Orders are dispatched by air or road depending on the destination,
                order size and urgency.
              </li>
              <li>
                <span className="font-semibold">Estimated Delivery Time:</span>{' '}
                Typical delivery time is{' '}
                <span className="font-semibold">2–4 working days after dispatch</span>, 
                depending on the courier partner and delivery location.
              </li>
            </ul>
          </div>
        </div>

        {/* Returns & Refunds */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-full bg-slate-900 flex items-center justify-center">
              <RotateCcw className="h-5 w-5 text-white" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900">
              Returns &amp; Refunds
            </h2>
          </div>
          <p className="text-sm text-slate-600 mb-2">
            We want you to be completely satisfied with your purchase.
          </p>
          <ul className="space-y-2 text-sm text-slate-600 mb-2">
            <li>
              <span className="font-semibold">Return Period:</span>{' '}
              If the product does not meet your expectations, you may return it
              within <span className="font-semibold">seven (7) days</span> of
              delivery.
            </li>
            <li>
              <span className="font-semibold">Condition:</span>{' '}
              Items must be unused, unwashed and returned with original tags and
              packaging intact.
            </li>
            <li>
              <span className="font-semibold">Refund Process:</span>{' '}
              Once the return is received and inspected, a refund will be issued
              to the original payment method, unless otherwise agreed in writing.
            </li>
            <li>
              <span className="font-semibold">Exclusions:</span>{' '}
              Certain items (for example, customised garments or items with
              hygiene/safety considerations) may be excluded from returns. Any
              such exclusions will be clearly communicated at the time of order.
            </li>
          </ul>
        </div>

        {/* Legal & Privacy */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Legal Disclaimer */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-slate-900 flex items-center justify-center">
                <ShieldCheck className="h-5 w-5 text-white" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900">
                Legal Disclaimer
              </h2>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed">
              Product colours may vary slightly due to photographic lighting or
              differences in monitor / display settings. While we strive to
              display our products as accurately as possible, we cannot guarantee
              that your device screen will represent the exact colour or texture
              of the physical product.
            </p>
          </div>

          {/* Privacy Policy */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-slate-900 flex items-center justify-center">
                <ShieldCheck className="h-5 w-5 text-white" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900">
                Privacy Policy
              </h2>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed mb-3">
              Sudharma Clothing Company Pvt. Ltd. is committed to protecting your
              privacy. We collect personal information (such as name, address and
              contact details) solely for the purpose of processing orders,
              communicating with you and improving our services.
            </p>
            <ul className="space-y-2 text-sm text-slate-600 mb-3">
              <li>
                We do <span className="font-semibold">not sell or rent</span>{' '}
                your information to third parties.
              </li>
              <li>
                We may share limited information with trusted service providers
                (such as payment processors, logistics and IT providers) only to
                fulfil your order or as required by law.
              </li>
              <li>
                Cookies or similar technologies may be used on our site to
                facilitate navigation, remember preferences and analyse usage in
                order to improve user experience.
              </li>
            </ul>
            <p className="text-xs text-slate-500">
              By using our website or contacting us via WhatsApp or social media,
              you consent to the collection and use of your information as
              described above. For any privacy-related questions, please contact
              us through the details provided on our Contact page.
            </p>
          </div>
        </div>

        <p className="mt-8 text-[11px] text-slate-500 text-center">
          This page is provided for general information and to support compliance
          with common online marketplace and messaging platform requirements. It
          does not replace independent legal advice.
        </p>
      </div>
    </section>
  );
};

export default PrivacyPolicy;
